import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, functions } from '../firebase/config';
import './SizeIssueReportPage.css';

const SizeIssueReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state?.orderData || {};
  
  const [formData, setFormData] = useState({
    orderNumber: orderData?.orderNumber || '',
    productName: orderData?.productName || '',
    sizeOrdered: orderData?.size || '',
    sizeReceived: '',
    issueType: '',
    description: '',
    contactEmail: orderData?.email || '',
    contactPhone: orderData?.phone || ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const issueTypes = [
    'Too tight',
    'Too loose', 
    'Too short',
    'Too long',
    'Wrong size delivered',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // File upload temporarily disabled

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit size issue report
      const sizeIssueData = {
        ...formData,
        photos: [], // No photos for now
        submittedAt: serverTimestamp(),
        status: 'pending',
        orderId: orderData?.id || null,
        userId: orderData?.userId || null
      };

      const docRef = await addDoc(collection(db, 'sizeIssues'), sizeIssueData);
      
      // Send email notifications using Firebase Functions
      try {
        const sendSizeIssueNotification = httpsCallable(functions, 'sendSizeIssueNotification');
        

        
        const result = await sendSizeIssueNotification({
          sizeIssueData: {
            ...sizeIssueData,
            reportId: docRef.id
          }
        });
        
      } catch (emailError) {
        console.error('Failed to send email notifications:', emailError);
        // Don't fail the form submission if email fails
      }
      
      setSubmitStatus('success');
      setTimeout(() => {
        navigate(-1); // Go back to previous page
      }, 2000);

    } catch (error) {
      console.error('Error submitting size issue:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="size-issue-page">
      <div className="size-issue-page-container">
        <div className="size-issue-page-header">
          <button 
            className="size-issue-back-btn" 
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ← Back
          </button>
          <h1 className="size-issue-page-title">Report a Size Issue</h1>
        </div>

        <form onSubmit={handleSubmit} className="size-issue-page-form">
          {/* Order Information - Hidden for logged-in users, shown for guests */}
          {!orderData?.orderNumber && (
            <div className="size-issue-section">
              <h3 className="size-issue-section-title">Order Information</h3>
              
              <div className="size-issue-row">
                <div className="size-issue-field">
                  <label className="size-issue-label">Order Number *</label>
                  <input
                    type="text"
                    name="orderNumber"
                    value={formData.orderNumber}
                    onChange={handleInputChange}
                    required
                    className="size-issue-input"
                  />
                </div>
                
                <div className="size-issue-field">
                  <label className="size-issue-label">Product Name *</label>
                  <input
                    type="text"
                    name="productName"
                    value={formData.productName}
                    onChange={handleInputChange}
                    required
                    className="size-issue-input"
                  />
                </div>
              </div>

              <div className="size-issue-row">
                <div className="size-issue-field">
                  <label className="size-issue-label">Size Ordered *</label>
                  <input
                    type="text"
                    name="sizeOrdered"
                    value={formData.sizeOrdered}
                    onChange={handleInputChange}
                    required
                    className="size-issue-input"
                  />
                </div>
                
                <div className="size-issue-field">
                  <label className="size-issue-label">Size Received *</label>
                  <input
                    type="text"
                    name="sizeReceived"
                    value={formData.sizeReceived}
                    onChange={handleInputChange}
                    required
                    className="size-issue-input"
                    placeholder="Enter the size you received"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Show order info for logged-in users */}
          {orderData?.orderNumber && (
            <div className="size-issue-section">
              <h3 className="size-issue-section-title">Order Details</h3>
              <div className="size-issue-order-summary">
                <div className="size-issue-order-item">
                  <span className="size-issue-order-label">Order:</span>
                  <span className="size-issue-order-value">#{orderData.orderNumber}</span>
                </div>
                <div className="size-issue-order-item">
                  <span className="size-issue-order-label">Product:</span>
                  <span className="size-issue-order-value">{orderData.productName}</span>
                </div>
                <div className="size-issue-order-item">
                  <span className="size-issue-order-label">Size Ordered:</span>
                  <span className="size-issue-order-value">{orderData.size}</span>
                </div>
                <div className="size-issue-field">
                  <label className="size-issue-label">Size Received *</label>
                  <input
                    type="text"
                    name="sizeReceived"
                    value={formData.sizeReceived}
                    onChange={handleInputChange}
                    required
                    className="size-issue-input"
                    placeholder="Enter the size you received"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="size-issue-section">
            <h3 className="size-issue-section-title">Issue Details</h3>
            
            <div className="size-issue-field">
              <label className="size-issue-label">Type of Issue *</label>
              <select
                name="issueType"
                value={formData.issueType}
                onChange={handleInputChange}
                required
                className="size-issue-select"
              >
                <option value="">Select an issue</option>
                {issueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div className="size-issue-field">
              <label className="size-issue-label">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                className="size-issue-textarea"
                rows="4"
                placeholder="Please describe the issue in detail"
              ></textarea>
            </div>

            {/* Image upload temporarily disabled */}
          </div>

          <div className="size-issue-section">
            <h3 className="size-issue-section-title">Resolution</h3>
            
            <div className="size-issue-resolution-info">
              <div className="size-issue-resolution-card">
                <div className="size-issue-resolution-icon">✂️</div>
                <div className="size-issue-resolution-content">
                  <h4>Free Alteration</h4>
                  <p>We'll provide complimentary alterations to ensure the perfect fit for your garment.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="size-issue-section">
            <h3 className="size-issue-section-title">Contact Information</h3>
            
            <div className="size-issue-row">
              <div className="size-issue-field">
                <label className="size-issue-label">Email *</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                  className="size-issue-input"
                />
              </div>
              
              <div className="size-issue-field">
                <label className="size-issue-label">Phone Number *</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  required
                  className="size-issue-input"
                />
              </div>
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="size-issue-success">
              <span className="size-issue-success-icon">✅</span>
              <p>Report submitted successfully!</p>
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="size-issue-error">
              <span className="size-issue-error-icon">❌</span>
              <p>Failed to submit report. Please try again.</p>
            </div>
          )}

          <button 
            type="submit" 
            className="size-issue-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SizeIssueReportPage;
